/**
 * Orden narrativo del timeline de guía (no solo cronológico por `at`).
 * 1) Tarjetas de guía: origen → transitoria → destino final
 * 2) Movimientos de bodega (cronológicos)
 * 3) Cambios de estado (cronológicos; guía consultada + destino final)
 */

import { normTimelineId } from './traceTimelineFilterIds';

const ROLE_ORDER = { origin: 0, intermediate: 1, final: 2 };

function folioSortKey(ev) {
	const f = ev.folio ?? ev.guide_number;
	if (f == null || f === '') return 'zzzz';
	const n = Number(f);
	return Number.isFinite(n) ? String(n).padStart(12, '0') : String(f);
}

function atSortKey(ev) {
	return ev?.at || '';
}

function anchorGuideId(trace, entityId) {
	if (trace?.anchor?.type === 'guide') {
		return normTimelineId(trace.anchor.id);
	}
	return normTimelineId(entityId);
}

function shouldIncludeStatusChange(ev, anchorId, guidesMap) {
	if (ev.kind !== 'status_change') return false;
	if (ev.scope === 'guide_detail') return false;
	const gid = normTimelineId(ev.guide_id);
	if (!gid) return false;
	if (gid === anchorId) return true;
	const role = guidesMap?.[gid]?.role;
	return role === 'final';
}

/**
 * @param {object|null} trace
 * @param {string|null} entityId
 * @returns {{ sections: { id: string, title: string, events: object[] }[] }}
 */
export function buildNarrativeGuideTimeline(trace, entityId) {
	const src = Array.isArray(trace?.timeline) ? trace.timeline : [];
	const anchorId = anchorGuideId(trace, entityId);
	const guidesMap = trace?.guides || {};

	const guideSteps = [];
	const movements = [];
	const statusChanges = [];

	for (const ev of src) {
		if (ev.kind === 'guide_step') {
			guideSteps.push(ev);
		} else if (ev.kind === 'movement') {
			movements.push(ev);
		} else if (shouldIncludeStatusChange(ev, anchorId, guidesMap)) {
			statusChanges.push(ev);
		}
	}

	guideSteps.sort((a, b) => {
		const ra = ROLE_ORDER[a.role] ?? 1;
		const rb = ROLE_ORDER[b.role] ?? 1;
		if (ra !== rb) return ra - rb;
		return folioSortKey(a).localeCompare(folioSortKey(b));
	});

	movements.sort((a, b) => atSortKey(a).localeCompare(atSortKey(b)));
	statusChanges.sort((a, b) => atSortKey(a).localeCompare(atSortKey(b)));

	const sections = [];

	if (guideSteps.length > 0) {
		sections.push({
			id: 'guides',
			title: 'Guías en la cadena',
			subtitle:
				'De origen a destino. La guía que abriste lleva la etiqueta Consultada.',
			events: guideSteps,
		});
	}

	const activity = [...movements, ...statusChanges].sort((a, b) =>
		atSortKey(a).localeCompare(atSortKey(b)),
	);

	if (activity.length > 0) {
		sections.push({
			id: 'activity',
			title: 'Movimientos y estados',
			subtitle:
				'Recepciones, envíos desde bodega y cambios de estado en el tiempo.',
			events: activity,
		});
	}

	return { sections, anchorId };
}

/** @deprecated Usar buildNarrativeGuideTimeline — mantiene compatibilidad plana */
export function buildFilteredGuideTimeline(trace, entityId) {
	const { sections } = buildNarrativeGuideTimeline(trace, entityId);
	return sections.flatMap(s => s.events);
}
